import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./ParkingSession.module.scss";
import Icon from "../../components/Icon";
import { Link, useNavigate, useParams } from "react-router";
import { downloadReceipt, getTicket } from "../../model/helpers/Session";
import dayjs from "dayjs";
import { calculateParkingTimes } from "../../model/helpers/DateHelper";
import ProgressBar from "../../components/ProgressBar/ProgressBar";
import { fetchData } from "../../services/api";
import { Form, Formik } from "formik";
import useBuyFormStore from "../../stores/useBuyFormStore";

const ParkingSession = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [shouldRender, setShouldRender] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState(null);
    const [ticketData, setTicketData] = useState(null);
    const [emailSession, setEmailSession] = useState(null);
    const [section, setSection] = useState(null);
    const [featureCode, setFeatureCode] = useState(null);
    const [featureType, setFeatureType] = useState(null);

    const { setLicencePlate, setEmail } = useBuyFormStore();

    let { ticketGUID } = useParams();

    useEffect(() => {
        getTicket(ticketGUID, (data) => {
            data.parkingFrom = dayjs(data.parkingFrom).toDate();
            data.parkingTo = dayjs(data.parkingTo).toDate();

            if (data.email) setEmailSession(data.email);

            if (data.sectionCode !== null) {
                setFeatureCode(data.sectionCode);
                setFeatureType("pz");
                fetchData("StreetParking/sections/1", "GET", "url", {
                    sectionCode: data.sectionCode,
                }).then((sectionsData) => {
                    if (!sectionsData[0]) {
                        navigate(`/`);
                    }
                    setSection(sectionsData[0]);
                });
            } else {
                setFeatureCode(data.parkMachineCode);
                setFeatureType("pm");
            }

            setTicketData(data);

            const { remaining } = calculateParkingTimes(data.parkingFrom, data.parkingTo);

            if (remaining > 0) {
                navigate(`/paid/${ticketGUID}`);
            } else {
                setShouldRender(true);
            }
        });
    }, [ticketGUID, navigate]);

    const handleDownloadReceipt = () => {
        downloadReceipt(ticketGUID);
    };

    const handleExtendClick = useCallback(() => {
        setLicencePlate(ticketData.licensePlate);
        if (emailSession) setEmail(emailSession);
        else setEmail("");
    }, [ticketData, setLicencePlate, setEmail]);

    const handleOrderTicket = (data) => {
        data.licencePlate = ticketData.licensePlate;
    };

    if (!shouldRender) {
        return null;
    }

    return (
        <Formik initialValues={{}} onSubmit={handleOrderTicket}>
            <div className={styles.containerWrapper}>
                <div className={styles.container}>
                    <Link to={"/"} className={styles.back}>
                        <Icon.CaretLeft color={"danger"} size={16} />
                        &nbsp; {t("parkingSession.back")}
                    </Link>

                    <h1 className={styles.header}>{t("parkingSession.title")}</h1>

                    <Form>
                        <div className={styles.item}>
                            <div className={`${styles.info}`}>
                                <ProgressBar orderDate={ticketData?.parkingFrom} endDate={ticketData?.parkingTo} />
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-md-6">
                                <div className={styles.item}>
                                    <div className={styles.info}>
                                        <div className={styles.icon}>
                                            <Icon.ParkingSign color={"#1D4F8B"} size={21} />
                                        </div>
                                        <div className={styles.textMuted}>
                                            {ticketData?.sectionCode
                                                ? t("home.MapSearchComponent.zones." + section?.sectionCategoryName)
                                                : t("home.MapSearchComponent.zones.parkingMachine")}
                                        </div>
                                        <div className={`${styles.itemEnd} ${styles.title} align-items-end`}>
                                            <div className={styles.itemTitle}>
                                                {ticketData?.sectionCode ?? ticketData.parkMachineCode}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className={styles.item}>
                                    <div className={styles.info}>
                                        <div className={styles.icon}>
                                            <Icon.Car color={"#1D4F8B"} size={21} />
                                        </div>
                                        <div className={styles.textMuted}>{t("parkingSession.car")}</div>
                                        <div className={`${styles.itemEnd} ${styles.title} align-items-end`}>
                                            <div className={styles.itemTitle}>{ticketData?.licensePlate}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-md-6">
                                <div className={styles.item}>
                                    <div className={styles.info}>
                                        <div className={styles.icon}>
                                            <Icon.PaymentCard color={"#1D4F8B"} size={21} />
                                        </div>
                                        <div className={styles.textMuted}>{t("parkingSession.paid.title")}</div>
                                        <div className={`${styles.itemEnd} ${styles.title} align-items-end`}>
                                            <div className={styles.itemTitle}>{t("parkingSession.paid.byCard")}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className={styles.item}>
                                    <div className={styles.info}>
                                        <div className={styles.icon}>
                                            <Icon.Pricing color={"#1D4F8B"} size={21} />
                                        </div>
                                        <div className={styles.textMuted}>{t("parkingSession.price")}</div>
                                        <div className={`${styles.itemEnd} ${styles.title} align-items-end`}>
                                            <div className={styles.itemTitle}>
                                                {ticketData?.priceTotal}&nbsp;{t("global.currency.czk")}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className={styles.formActions}>
                            <button
                                type="submit"
                                className={`${styles.actionBtn} ${styles.defaultBtn} ${styles.text500}`}
                                onClick={() => handleDownloadReceipt()}
                            >
                                <Icon.Document color={"black"} size={16} />
                                &nbsp;&nbsp;{t("paid.invoice")}
                            </button>

                            {featureType &&
                                featureCode &&
                                ticketData?.licensePlate &&
                                window._env_.REACT_APP_MAINTENANCE_MODE !== "true" && (
                                    <Link
                                        to={`/buy/${featureType}/${featureCode}`}
                                        className={`${styles.buyButton}`}
                                        onClick={handleExtendClick}
                                    >
                                        <Icon.Refresh color={"#FFFFFF"} size={16} />
                                        &nbsp;&nbsp;{t("parkingSession.buyAgain")}
                                    </Link>
                                )}
                        </div>
                    </Form>
                </div>
            </div>
        </Formik>
    );
};

export default ParkingSession;
